import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { publicApi } from "@/lib/api";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import PublicLayout from "@/components/public/PublicLayout";
import PreviewBanner from "@/components/public/PreviewBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BRANDS, BUDGET_OPTIONS, TIMELINE_OPTIONS, INTEREST_OPTIONS, CONTACT_OPTIONS } from "@/lib/format";
import { usePageIntro, PAGE_INTROS } from "@/lib/cms";
import { toast } from "sonner";
import { Check, ArrowRight, Crown } from "lucide-react";

const EMPTY = {
  name: "", email: "", phone: "", email_consent: true, sms_consent: false,
  preferred_contact: "email", favorite_brands: [], budget_range: "", buying_timeline: "",
  interest_type: "", watch_preferences: "", merch_interest: false, create_account: false,
  password: "", notes: "",
};

function Field({ label, children }) {
  return (<div className="space-y-2"><Label className="text-[var(--text-2)] text-sm">{label}</Label>{children}</div>);
}

export default function VipJoin() {
  const navigate = useNavigate();
  const { setSession } = useCustomerAuth();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [intro, preview] = usePageIntro(PAGE_INTROS.vip.key, PAGE_INTROS.vip.defaults);

  const toggleBrand = (b) =>
    set("favorite_brands", form.favorite_brands.includes(b)
      ? form.favorite_brands.filter((x) => x !== b)
      : [...form.favorite_brands, b]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
    if (form.create_account && form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.create_account) delete payload.password;
      const r = await publicApi.post("/public/vip-signup", payload);
      if (r.data.account_created && r.data.token) {
        setSession(r.data.token, r.data.customer);
      }
      setResult(r.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  if (result) {
    return (
      <PublicLayout>
        <section className="max-w-2xl mx-auto px-5 py-24 text-center" data-testid="vip-confirmation">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full border border-[#c9a86a] text-[#c9a86a] mb-6">
            <Check size={28} />
          </div>
          <div className="text-[#c9a86a] tracking-[0.3em] text-xs mb-3">YOU ARE ON THE LEDGER</div>
          <h1 className="text-3xl md:text-4xl font-semibold text-[var(--text)]">Welcome to the house.</h1>
          <div className="mt-8 border border-[#c9a86a] bg-[var(--panel-2)] rounded-lg p-6 inline-block">
            <div className="flex items-center justify-center gap-2 text-[#c9a86a]">
              <Crown size={18} /><span className="tracking-widest text-sm">YOUR STARTING TIER</span>
            </div>
            <div className="text-2xl font-semibold text-[var(--text)] mt-2" data-testid="confirmation-tier">{result.tier_name}</div>
          </div>
          <p className="text-[var(--text-2)] mt-8 leading-relaxed">
            A member of the house will reach out personally. Your place on the ledger rises with your taste and engagement.
          </p>
          {result.account_created ? (
            <div className="mt-8">
              <p className="text-sm text-[var(--muted)] mb-3">Your client account is ready.</p>
              <Button onClick={() => navigate("/account")} data-testid="go-to-account"
                className="bg-[#c9a86a] hover:bg-[#b89455] text-[var(--on-gold)] font-semibold rounded-none px-8 py-6">
                Enter Your Account <ArrowRight size={18} className="ml-2" />
              </Button>
            </div>
          ) : result.account_message ? (
            <div className="mt-8">
              <p className="text-sm text-amber-400/90 mb-3">{result.account_message}</p>
              <Button onClick={() => navigate("/account/login")} className="bg-[#c9a86a] text-[var(--on-gold)] rounded-none px-8 py-6">Sign In</Button>
            </div>
          ) : (
            <div className="mt-8">
              <Button onClick={() => navigate("/")} variant="outline" className="border-[var(--border-2)] text-[var(--text-2)] rounded-none px-8">Back to Home</Button>
            </div>
          )}
        </section>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <section className="max-w-3xl mx-auto px-5 py-16">
        <div className="text-center mb-10">
          <div className="text-[#c9a86a] tracking-[0.3em] text-xs mb-3">{intro.eyebrow}</div>
          <h1 className="text-3xl md:text-4xl font-semibold text-[var(--text)]">{intro.title}</h1>
          <p className="text-[var(--text-2)] mt-4">{intro.subtitle}</p>
        </div>

        <form onSubmit={submit} className="bg-[var(--panel-2)] border border-[var(--border-2)] rounded-xl p-6 md:p-8 space-y-6" data-testid="vip-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Full Name *"><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-[var(--bg)] border-[var(--border-2)] text-[var(--text)]" data-testid="vip-name" /></Field>
            <Field label="Email *"><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-[var(--bg)] border-[var(--border-2)] text-[var(--text)]" data-testid="vip-email" /></Field>
            <Field label="Phone"><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-[var(--bg)] border-[var(--border-2)] text-[var(--text)]" data-testid="vip-phone" /></Field>
            <Field label="Preferred Contact">
              <Select value={form.preferred_contact} onValueChange={(v) => set("preferred_contact", v)}>
                <SelectTrigger className="bg-[var(--bg)] border-[var(--border-2)] text-[var(--text)]" data-testid="vip-contact"><SelectValue /></SelectTrigger>
                <SelectContent>{CONTACT_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Favorite Brands">
            <div className="flex flex-wrap gap-2">
              {BRANDS.map((b) => {
                const on = form.favorite_brands.includes(b);
                return (
                  <button type="button" key={b} onClick={() => toggleBrand(b)} data-testid={`brand-${b}`}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${on ? "bg-[#c9a86a] text-[var(--on-gold)] border-[#c9a86a]" : "bg-[var(--bg)] text-[var(--text-2)] border-[var(--border-2)] hover:border-[#c9a86a]"}`}>
                    {b}
                  </button>
                );
              })}
            </div>
          </Field>

          <div className="grid sm:grid-cols-3 gap-5">
            <Field label="Budget Range">
              <Select value={form.budget_range} onValueChange={(v) => set("budget_range", v)}>
                <SelectTrigger className="bg-[var(--bg)] border-[var(--border-2)] text-[var(--text)]" data-testid="vip-budget"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{BUDGET_OPTIONS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Buying Timeline">
              <Select value={form.buying_timeline} onValueChange={(v) => set("buying_timeline", v)}>
                <SelectTrigger className="bg-[var(--bg)] border-[var(--border-2)] text-[var(--text)]" data-testid="vip-timeline"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{TIMELINE_OPTIONS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Interest Type">
              <Select value={form.interest_type} onValueChange={(v) => set("interest_type", v)}>
                <SelectTrigger className="bg-[var(--bg)] border-[var(--border-2)] text-[var(--text)]" data-testid="vip-interest"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>{INTEREST_OPTIONS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Watch Preferences & Wishlist">
            <Textarea rows={3} value={form.watch_preferences} onChange={(e) => set("watch_preferences", e.target.value)}
              placeholder="Models you're chasing, dial colors, full set only, etc." className="bg-[var(--bg)] border-[var(--border-2)] text-[var(--text)]" data-testid="vip-prefs" />
          </Field>

          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer"><Checkbox checked={form.email_consent} onCheckedChange={(v) => set("email_consent", !!v)} data-testid="vip-email-consent" /><span className="text-sm text-[var(--text-2)]">Email me drops, matches & house news</span></label>
            <label className="flex items-center gap-3 cursor-pointer"><Checkbox checked={form.sms_consent} onCheckedChange={(v) => set("sms_consent", !!v)} data-testid="vip-sms-consent" /><span className="text-sm text-[var(--text-2)]">Text me time-sensitive first calls</span></label>
            <label className="flex items-center gap-3 cursor-pointer"><Checkbox checked={form.merch_interest} onCheckedChange={(v) => set("merch_interest", !!v)} /><span className="text-sm text-[var(--text-2)]">I'm interested in LCH merch drops</span></label>
          </div>

          <div className="border-t border-[var(--border-2)] pt-5">
            <label className="flex items-center gap-3 cursor-pointer"><Checkbox checked={form.create_account} onCheckedChange={(v) => set("create_account", !!v)} data-testid="vip-create-account" /><span className="text-sm text-[var(--text)]">Create a client account so I can track everything</span></label>
            {form.create_account && (
              <div className="mt-4"><Field label="Choose a Password"><Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} className="bg-[var(--bg)] border-[var(--border-2)] text-[var(--text)]" data-testid="vip-password" /></Field></div>
            )}
          </div>

          <Field label="Notes / Special Requests">
            <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="bg-[var(--bg)] border-[var(--border-2)] text-[var(--text)]" data-testid="vip-notes" />
          </Field>

          <Button type="submit" disabled={busy} data-testid="vip-submit"
            className="w-full bg-[#c9a86a] hover:bg-[#b89455] text-[var(--on-gold)] font-semibold tracking-wide rounded-none py-6 text-base">
            {busy ? "Submitting…" : "Join the VIP List"}
          </Button>
        </form>
      </section>
      {preview && <PreviewBanner testId="vip-preview-banner" />}
    </PublicLayout>
  );
}
